// Storage for a drivetrack_admin invite token handed off across the create-account/
// confirm-email/sign-in round trip. localStorage, not sessionStorage — same reasoning as
// pendingInstructorInvite.ts: the confirmation email link is commonly opened in a brand new
// browser tab, which gets a fresh, empty sessionStorage for this origin even though it's the
// same browser, silently losing the token and the invitation never actually getting accepted.

const TOKEN_KEY    = 'dt_pending_admin_invite_token'
const SAVED_AT_KEY = 'dt_pending_admin_invite_saved_at'

export const PENDING_ADMIN_INVITE_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours

/** Pure: is a token saved at `savedAt` still fresh enough to act on, judged at `now`? */
export function isPendingAdminInviteFresh(savedAt: number, now: number): boolean {
  if (!Number.isFinite(savedAt) || !Number.isFinite(now)) return false
  const age = now - savedAt
  return age >= 0 && age < PENDING_ADMIN_INVITE_MAX_AGE_MS
}

export function savePendingAdminInviteToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(SAVED_AT_KEY, String(Date.now()))
  } catch { /* storage unavailable — token still usable for the rest of this page's lifetime */ }
}

/** Returns the token only if present and still fresh; clears it (and the timestamp) otherwise. */
export function readPendingAdminInviteToken(now: number = Date.now()): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return null
    const savedAt = Number(localStorage.getItem(SAVED_AT_KEY))
    if (!isPendingAdminInviteFresh(savedAt, now)) {
      clearPendingAdminInviteToken()
      return null
    }
    return token
  } catch {
    return null
  }
}

export function clearPendingAdminInviteToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(SAVED_AT_KEY)
  } catch { /* ignore */ }
}
